import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { eventService, gymnastService, clubService, judgeService, levelService, appService, judgeConfigService } from '../services/supabaseService';
import { supabase } from '../config/supabase';
import { imageServiceProxy, storageServiceProxy } from '../services/proxyService';
import { useNotifications } from '../components/shared/useNotifications';
import { useConfirm } from '../components/shared/ConfirmContext';
import { getApparatusForEvent } from '../utils/disciplineUtils';
import { getStandardTemplate, calculateTotal } from '../utils/scoringTemplates';
import { DISCIPLINE_TABS } from '../utils/constants';
import JudgeEventSetup from '../components/judge/JudgeEventSetup';
import Header from '../components/shared/Header';
import CategoryFooter from '../components/shared/CategoryFooter';
import './JudgeScoreEntry.css';
import { Target, Save, ChevronLeft, ArrowLeft, Settings, Edit2, CheckCircle, Trash2, RefreshCw } from 'lucide-react';
import logger from '../utils/logger';

const getDisciplineDisplay = (discipline, category) => {
  if (!discipline) return { fullName: 'General', logo: null, name: '' };
  const match = DISCIPLINE_TABS.find(t =>
    t.discipline === discipline && (t.category === (category || '') || (!category && !t.category))
  );
  return match || { fullName: discipline, logo: null, name: '' };
};

const JudgeScoreEntry = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  const confirm = useConfirm();

  // Estado del juez
  const [judgeInfo, setJudgeInfo] = useState(null);
  
  // Estado del evento
  const [event, setEvent] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [gymnasts, setGymnasts] = useState([]);
  
  // Estado de filtros
  const [selectedClub, setSelectedClub] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [selectedFranja, setSelectedFranja] = useState(null);
  const [selectedApparatus, setSelectedApparatus] = useState(null);
  
  // Estado de puntajes
  const [scores, setScores] = useState({});
  const [savingStatus, setSavingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [athleteImages, setAthleteImages] = useState({});
  const [clubImages, setClubImages] = useState({});
  const [eventImage, setEventImage] = useState(null);
  
  // Setup / Config state
  const [showSetup, setShowSetup] = useState(false);
  const [judgeConfigs, setJudgeConfigs] = useState({}); // {apparatus_code: {columns, template_type}}
  const [activeScoreColumns, setActiveScoreColumns] = useState([]); // columns for currently selected apparatus
  
  // Constantes - Los niveles y franjas dependen del evento
  // Se obtienen del evento una vez cargado
  const [levelOptions, setLevelOptions] = useState([]);
  const [franjaOptions, setFranjaOptions] = useState([]);

  // Verificar autenticación
  useEffect(() => {
    const judgeId = localStorage.getItem('judgeId');
    const isJudge = localStorage.getItem('isJudge');

    if (!isJudge || !judgeId) {
      navigate('/jueces');
      return;
    }

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargar datos del evento y juez
  const loadData = async () => {
    try {
      const judgeId = localStorage.getItem('judgeId');
      
      // Cargar información del juez
      const { data: judge } = await judgeService.getById(judgeId);
      if (judge) {
        setJudgeInfo(judge);
      }

      // Cargar información del evento
      const { data: eventData } = await eventService.getById(eventId);
      if (eventData) {
        setEvent(eventData);

        // Cargar imagen del evento
        try {
          // Try images table first
          const { data: imgUrl } = await imageServiceProxy.getByEntity('event', eventId);
          if (imgUrl) {
            setEventImage(imgUrl);
          } else {
            // Fallback: storage
            const { data: evtUrl } = await storageServiceProxy.getEventImage(String(eventId));
            if (evtUrl) setEventImage(evtUrl);
          }
        } catch (imgErr) {
        }
        
        // Cargar niveles dinámicamente desde la BD
        const { data: eventLevels } = await levelService.getEventLevels(eventId);
        if (eventLevels && eventLevels.length > 0) {
          const levelNames = eventLevels.map(l => l.name);
          setLevelOptions(levelNames);
        } else {
          // Si no hay niveles asignados, usar valores por defecto
          const levels = eventData.levels && Array.isArray(eventData.levels) 
            ? eventData.levels 
            : ['Iniciante', 'Intermedio', 'Avanzado'];
          setLevelOptions(levels);
        }
        
        // Obtener franjas del evento (si están definidas)
        const franjas = eventData.franjas && Array.isArray(eventData.franjas)
          ? eventData.franjas
          : ['Franja 1', 'Franja 2', 'Franja 3'];
        setFranjaOptions(franjas);
        
        // Cargar clubes del evento
        const { data: clubsData } = await clubService.getByEvent(eventId);
        if (clubsData) {
          setClubs(clubsData);
          
          // Cargar imágenes de los clubes
          const images = {};
          
          // Try images table first, then storage fallback
          for (const club of clubsData) {
            try {
              const { data: imgUrl } = await imageServiceProxy.getByEntity('club', club.id);
              if (imgUrl) {
                images[club.id] = imgUrl;
              }
            } catch (e) {
              // Try storage fallback
              try {
                const { data: logoUrl } = await storageServiceProxy.getClubLogo(String(club.id));
                if (logoUrl) images[club.id] = logoUrl;
              } catch (_) {}
            }
          }
          
          setClubImages(images);
        }

        // Load judge configs for this event
        if (judge) {
          try {
            const { data: configs } = await judgeConfigService.getConfigsByEvent(judge.id, eventId);
            const configMap = {};
            (configs || []).forEach(c => {
              configMap[c.apparatus] = { columns: c.columns, template_type: c.template_type, judge_role: c.judge_role || 'standard' };
            });
            setJudgeConfigs(configMap);
          } catch (cfgErr) {
          }
        }
      } else {
        notifyError('No se pudo cargar el evento');
        navigate('/jueces/dashboard');
      }
      
      setLoading(false);
    } catch (err) {
      logger.error('Error cargando datos:', err?.message);
      notifyError('Error al cargar los datos');
      setLoading(false);
    }
  };

  // Auto-cargar config guardada o abrir modal si no hay ninguna
  useEffect(() => {
    if (loading || !event || !judgeInfo) return;

    const configKeys = Object.keys(judgeConfigs);
    if (configKeys.length === 0) {
      // Sin config — abrir modal obligatorio
      setShowSetup(true);
    } else if (!selectedApparatus) {
      // Tiene config pero no ha seleccionado aparato — precargar el primero
      const firstApp = configKeys[0];
      const config = judgeConfigs[firstApp];
      if (config && config.columns && config.columns.length > 0) {
        setSelectedApparatus(firstApp);
        setActiveScoreColumns(config.columns);
      }
    }
  }, [loading, event, judgeInfo, judgeConfigs, selectedApparatus]);

  // Cargar gimnastas cuando cambian los filtros
  useEffect(() => {
    const loadGymnasts = async () => {
      if (!selectedClub) {
        setGymnasts([]);
        return;
      }

      try {
        const { data: gymnastsData } = await gymnastService.getByClub(selectedClub);
        
        if (gymnastsData) {
          let filtered = gymnastsData;
          
          // Filtrar por nivel
          if (selectedLevel) {
            filtered = filtered.filter(g => g.level === selectedLevel);
          }
          
          // Filtrar por franja
          if (selectedFranja) {
            filtered = filtered.filter(g => g.franja === selectedFranja);
          }
          
          setGymnasts(filtered);
          
          // Cargar imágenes de los atletas - optimizado
          const images = {};
          
          // Obtener lista de archivos de la carpeta gymnasts una sola vez
          const { data: files } = await storageServiceProxy.list('gymnastics-images', 'gymnasts', 1000);
          
          if (files && files.length > 0) {
            // Crear mapa de ID a archivo
            for (const gymnast of filtered) {
              const matchingFile = files.find(file => file.name.includes(gymnast.id));
              if (matchingFile) {
                const { data: publicUrlData } = await storageServiceProxy.getPublicUrl('gymnastics-images', `gymnasts/${matchingFile.name}`);
                if (publicUrlData && publicUrlData.publicUrl) {
                  images[gymnast.id] = publicUrlData.publicUrl;
                }
              }
            }
          }
          
          setAthleteImages(images);
        }
      } catch (err) {
        logger.error('Error cargando gimnastas:', err?.message);
      }
    };

    loadGymnasts();
  }, [selectedClub, selectedLevel, selectedFranja]);

  // Obtener aparatos disponibles
  const availableApparatus = useMemo(() => {
    if (event && event.discipline && event.category) {
      return getApparatusForEvent(event);
    }
    return [];
  }, [event]);

  // Handle apparatus selection — load config or open setup
  const handleApparatusSelect = (appCode) => {
    if (!appCode) {
      setSelectedApparatus(null);
      setActiveScoreColumns([]);
      return;
    }
    setSelectedApparatus(appCode);
    const config = judgeConfigs[appCode];
    if (config && config.columns && config.columns.length > 0) {
      setActiveScoreColumns(config.columns);
    } else {
      // No config — open setup
      setActiveScoreColumns([]);
      setShowSetup(true);
    }
  };

  // Handle setup completion
  const handleSetupConfigured = (appCode, columns, templateType) => {
    setJudgeConfigs({
      [appCode]: { columns, template_type: templateType, judge_role: 'standard' }
    });
    setActiveScoreColumns(columns);
    setSelectedApparatus(appCode);
    setShowSetup(false);
  };

  // Eliminar configuración de un aparato y reabrir setup
  const handleDeleteConfig = async () => {
    if (!selectedApparatus || !judgeInfo) return;
    
    const title = hasUnsavedScores ? '⚠️ Puntajes sin guardar' : 'Eliminar configuración';
    const message = hasUnsavedScores
      ? 'Tienes puntajes sin guardar.\n\nEliminar la configuración también descartará los puntajes no guardados.\n\n¿Deseas continuar?'
      : '¿Eliminar esta configuración? Podrás crearla de nuevo.';
    const ok = await confirm({
      title,
      message,
      type: 'danger',
      confirmText: 'Eliminar'
    });
    if (!ok) return;

    try {
      await judgeConfigService.deleteConfig(judgeInfo.id, eventId, selectedApparatus);
    } catch (err) {
    }

    // Limpiar del estado local
    setJudgeConfigs(prev => {
      const updated = { ...prev };
      delete updated[selectedApparatus];
      return updated;
    });
    setActiveScoreColumns([]);
    setSelectedApparatus(null);
    setShowSetup(true);
  };

  // Cambiar de aparato/disciplina — abre el modal de config
  const handleChangeApparatus = async () => {
    if (hasUnsavedScores) {
      const ok = await confirm({
        title: '⚠️ Puntajes sin guardar',
        message: 'Tienes puntajes sin guardar para este aparato.\n\nSi cambias ahora perderás los datos ingresados.\n\n¿Deseas continuar?',
        type: 'warning',
        confirmText: 'Cambiar aparato'
      });
      if (!ok) return;
    }
    setShowSetup(true);
  };

  // Nombre del aparato seleccionado
  const currentApparatusName = useMemo(() => {
    if (!selectedApparatus) return null;
    const app = availableApparatus.find(a => a.code === selectedApparatus);
    return app ? app.name : selectedApparatus;
  }, [selectedApparatus, availableApparatus]);

  // Rol actual del juez para el aparato seleccionado
  const currentJudgeRole = judgeConfigs[selectedApparatus]?.judge_role || null;

  // Manejar cambio de puntaje — ahora soporta multi-columna
  const handleScoreChange = (gymnastId, columnKey, value) => {
    // Validar: máximo 99.999
    const regex = /^(\d{0,2}(\.\d{0,3})?)?$/;
    if (value === '' || regex.test(value)) {
      setScores(prev => ({
        ...prev,
        [gymnastId]: {
          ...(prev[gymnastId] || {}),
          [columnKey]: value
        }
      }));
    }
  };

  // Guardar todos los puntajes modificados de la sección actual
  const handleSaveSectionScores = async () => {
    if (Object.keys(scores).length === 0) {
      notifyError('No hay puntajes para guardar');
      return;
    }

    setSavingStatus('saving');

    try {
      const updates = [];
      
      // Preparar datos para guardar
      for (const [gymnastId, colScores] of Object.entries(scores)) {
        const formattedScores = {};
        
        if (activeScoreColumns.length > 0 && selectedApparatus) {
          // Multi-column mode: compute total from configured columns
          const total = calculateTotal(activeScoreColumns, colScores);
          formattedScores[selectedApparatus] = parseFloat(total.toFixed(2));
          
          // Also save the individual column values under a detail key for future reference
          const detailKey = `${selectedApparatus}_detail`;
          const detailValues = {};
          activeScoreColumns.forEach(col => {
            const val = parseFloat(colScores[col.key]);
            if (!isNaN(val)) detailValues[col.key] = parseFloat(val.toFixed(3));
          });
          if (Object.keys(detailValues).length > 0) {
            formattedScores[detailKey] = detailValues;
          }
        } else {
          // Legacy single-column mode
          for (const [key, score] of Object.entries(colScores)) {
            if (score !== '' && score !== null) {
              const numScore = parseFloat(score);
              if (!isNaN(numScore)) {
                formattedScores[key] = parseFloat(numScore.toFixed(2));
              }
            }
          }
        }
        
        if (Object.keys(formattedScores).length > 0) {
          updates.push({
            gymnastId,
            scores: formattedScores
          });
        }
      }

      // Guardar todos los puntajes en gimnasts (scores actual)
      for (const update of updates) {
        const { error } = await gymnastService.updateScore(update.gymnastId, update.scores);
        if (error) {
          throw new Error(error);
        }
      }

      // También guardar en competition_scores para historial
      for (const update of updates) {
        const gymnast = gymnasts.find(g => g.id === update.gymnastId);
        const clubId = gymnast?.club_id;
        
        if (clubId && Object.keys(update.scores).length > 0) {
          await supabase
            .from('competition_scores')
            .upsert({
              gymnast_id: update.gymnastId,
              event_id: eventId,
              club_id: clubId,
              scores: update.scores,
              total: Object.values(update.scores).reduce((sum, s) => sum + (Number(s) || 0), 0),
              updated_at: new Date().toISOString()
            }, { onConflict: 'gymnast_id,event_id' });
        }
      }

      setSavingStatus('saved');
      notifySuccess(`${updates.length} atleta(s) actualizado(s)`);
      
      // Recargar gimnastas para mostrar datos actualizados
      try {
        const { data: updatedGymnasts } = await gymnastService.getByClub(selectedClub);
        
        if (updatedGymnasts) {
          let filtered = updatedGymnasts;
          
          // Aplicar los mismos filtros que estaban activos
          if (selectedLevel) {
            filtered = filtered.filter(g => g.level === selectedLevel);
          }
          
          if (selectedFranja) {
            filtered = filtered.filter(g => g.franja === selectedFranja);
          }
          
          setGymnasts(filtered);
        }
      } catch (reloadError) {
      }
      
      // Limpiar estado
      setScores({});
      
      // Resetear estado de guardado después de 2 segundos
      setTimeout(() => {
        setSavingStatus(null);
      }, 2000);
    } catch (err) {
      logger.error('Error guardando puntajes:', err?.message);
      setSavingStatus('error');
      notifyError('Error al guardar los puntajes');
      setTimeout(() => {
        setSavingStatus(null);
      }, 2000);
    }
  };

  // Detectar si hay puntajes sin guardar
  const hasUnsavedScores = Object.keys(scores).some(gymnastId => {
    const gymnastScores = scores[gymnastId];
    return Object.values(gymnastScores || {}).some(v => v !== '' && v !== undefined);
  });

  // Prevenir cierre/recarga del navegador si hay datos sin guardar
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedScores) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedScores]);

  // Volver atrás — confirmar si hay puntajes sin guardar
  const handleGoBack = async () => {
    if (hasUnsavedScores) {
      const ok = await confirm({
        title: '⚠️ Puntajes sin guardar',
        message: 'Tienes puntajes sin guardar.\n\nSi sales ahora perderás los datos ingresados.\n\n¿Deseas abandonar sin guardar?',
        type: 'warning',
        confirmText: 'Salir sin guardar'
      });
      if (!ok) return;
    }
    navigate('/jueces/dashboard');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', paddingTop: '50px' }}>
        <div className="spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div style={{ textAlign: 'center', paddingTop: '50px' }}>
        <p>No se encontró el evento</p>
        <button 
          onClick={handleGoBack}
          style={{
            marinTop: '20px',
            padding: '10px 20px',
            background: '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          Volver
        </button>
      </div>
    );
  }

  const disc = getDisciplineDisplay(event.discipline, event.category);
  const selectedClubData = clubs.find(c => c.id === selectedClub);

  return (
    <div className="judge-score-entry-container">
      <Header />
      
      <div className="judge-score-entry-content">
        {/* Header */}
        <div className="score-entry-header">
          <div className="header-left">
            <button onClick={handleGoBack} className="back-button">
              <ArrowLeft size={20} />
            </button>
            {eventImage && (
              <img src={eventImage} alt={event.name} className="jse-event-logo" />
            )}
            <div>
              <h1>Ingreso de Puntajes</h1>
              <p className="event-name">{event.name}</p>
            </div>
          </div>
          <div className="header-info">
            {disc.logo && <img src={disc.logo} alt={disc.name} className="jse-discipline-logo" />}
            <div className="jse-discipline-info">
              <span className="jse-discipline-abbr">{disc.name || 'General'}</span>
              <span className="jse-discipline-full">{disc.fullName}</span>
            </div>
          </div>
        </div>

        {/* Banner unificado — Evaluacion */}
        <div className={`jse-banner-unified ${selectedApparatus && activeScoreColumns.length > 0 ? 'jse-banner-active' : 'jse-banner-discipline'}`}>
          {selectedApparatus && activeScoreColumns.length > 0 ? (
            <>
              <div className="jse-evaluating-text">
                <span className="jse-evaluating-label">Usted está evaluando</span>
                <span className="jse-evaluating-apparatus">{currentApparatusName}</span>
                {currentJudgeRole && (
                  <span className={`jse-role-badge ${currentJudgeRole === 'standard' ? 'jse-role-standard' : ''}`}>
                    {currentJudgeRole === 'standard' ? '⚫ Evaluación Estándar' : `Panel ${currentJudgeRole}`}
                  </span>
                )}
              </div>
              <div className="jse-evaluating-actions">
                {selectedClubData && (
                  <div className="jse-selected-club">
                    {clubImages[selectedClubData.id] && (
                      <div className="jse-club-avatar">
                        <img src={clubImages[selectedClubData.id]} alt={selectedClubData.name} />
                      </div>
                    )}
                    <div className="jse-club-info">
                      <span className="jse-club-label">Club</span>
                      <span className="jse-club-name">{selectedClubData.name}</span>
                    </div>
                  </div>
                )}
                <button className="jse-change-apparatus-btn" onClick={handleChangeApparatus} title="Configura de nuevo los parámetros de evaluación">
                  <Settings size={14} /> Configurar Evaluacion
                </button>
                <button className="jse-delete-config-btn" onClick={handleDeleteConfig} title="Eliminar esta configuración">
                  <Trash2 size={14} /> Eliminar configuracion
                </button>
              </div>
            </>
          ) : (
            selectedClubData && (
              <div className="jse-selected-club">
                {clubImages[selectedClubData.id] && (
                  <div className="jse-club-avatar">
                    <img src={clubImages[selectedClubData.id]} alt={selectedClubData.name} />
                  </div>
                )}
                <div className="jse-club-info">
                  <span className="jse-club-label">Club seleccionado</span>
                  <span className="jse-club-name">{selectedClubData.name}</span>
                </div>
              </div>
            )
          )}
        </div>

        {/* Filtros — sin aparato (se elige en el modal de config) */}
        <div className="filters-section">
          <div className="filter-group">
            <label>Club</label>
            <select
              value={selectedClub || ''}
              onChange={(e) => {
                setSelectedClub(e.target.value);
                setSelectedLevel(null);
                setSelectedFranja(null);
              }}
              className="filter-select"
            >
              <option value="">-- Selecciona un club --</option>
              {clubs.map(club => (
                <option key={club.id} value={club.id}>
                  {club.name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Nivel</label>
            <select
              value={selectedLevel || ''}
              onChange={(e) => setSelectedLevel(e.target.value)}
              disabled={!selectedClub}
              className="filter-select"
            >
              <option value="">-- Todos los niveles --</option>
              {levelOptions.map(level => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Franja</label>
            <select
              value={selectedFranja || ''}
              onChange={(e) => setSelectedFranja(e.target.value)}
              disabled={!selectedClub}
              className="filter-select"
            >
              <option value="">-- Todas las franjas --</option>
              {franjaOptions.map(franja => (
                <option key={franja} value={franja}>
                  {franja}
                </option>
              ))}
            </select>
          </div>
        </div>


        {/* Tabla de atletas */}
        {selectedClub && gymnasts.length > 0 ? (
          <div className="gymnasts-section">
            <h2>Atletas ({gymnasts.length})</h2>
            
            {selectedApparatus && activeScoreColumns.length > 0 ? (
              /* ===== MULTI-COLUMN SCORING TABLE ===== */
              <div className="scores-table-wrapper">
                <table className="scores-table-editable jse-multi-col">
                  <thead>
                    <tr>
                      <th className="name-column">Atleta</th>
                      <th>Nivel</th>
                      <th>Franja</th>
                      {activeScoreColumns.map(col => (
                        <th key={col.key} className={`score-column jse-col-${col.type}`} title={col.description || col.label}>
                          <div className="jse-col-header">
                            <span className={`jse-col-type-dot jse-dot-${col.type}`}></span>
                            {col.label}
                          </div>
                        </th>
                      ))}
                      <th className="score-column jse-col-total">Total</th>
                      <th className="status-column"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {gymnasts.map(gymnast => {
                      const gymnastScores = scores[gymnast.id] || {};
                      // Calculate live total from entered sub-scores
                      const liveTotal = calculateTotal(activeScoreColumns, gymnastScores);
                      const savedTotal = gymnast.scores?.[selectedApparatus];
                      const hasAnyInput = activeScoreColumns.some(col => gymnastScores[col.key] !== undefined && gymnastScores[col.key] !== '');
                      
                      return (
                        <tr key={gymnast.id}>
                          <td className="name-column">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              {athleteImages[gymnast.id] ? (
                                <img 
                                  src={athleteImages[gymnast.id]} 
                                  alt={gymnast.name}
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '2px solid #e5e7eb'
                                  }}
                                />
                              ) : (
                                <div style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '50%',
                                  background: '#f3f4f6',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '16px',
                                  color: '#9ca3af'
                                }}>
                                  👤
                                </div>
                              )}
                              <span>{gymnast.name}</span>
                            </div>
                          </td>
                          <td className="level-column">{gymnast.level}</td>
                          <td className="franja-column">{gymnast.franja}</td>
                          {activeScoreColumns.map(col => {
                            const val = gymnastScores[col.key] ?? '';
                            return (
                              <td key={col.key} className={`score-column jse-input-cell jse-cell-${col.type}`}>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={val}
                                  onChange={(e) => handleScoreChange(gymnast.id, col.key, e.target.value)}
                                  className={`score-input-table jse-input-${col.type}`}
                                  placeholder={col.type === 'deduction' || col.type === 'penalty' ? '−0.0' : '0.00'}
                                  title={col.label}
                                  maxLength="6"
                                />
                              </td>
                            );
                          })}
                          <td className="score-column jse-total-cell">
                            <span className={`jse-total-value ${hasAnyInput ? 'jse-total-active' : ''}`}>
                              {hasAnyInput ? liveTotal.toFixed(2) : (savedTotal !== undefined && savedTotal !== null ? parseFloat(savedTotal).toFixed(2) : '—')}
                            </span>
                          </td>
                          <td className="status-column">
                            {!hasAnyInput && (savedTotal === undefined || savedTotal === null || savedTotal === 0) && (
                              <span className="warning-badge" title="Sin puntaje">⚠️</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : selectedApparatus && activeScoreColumns.length === 0 ? (
              /* ===== NO CONFIG — PROMPT SETUP ===== */
              <div className="jse-setup-prompt">
                <Settings size={48} strokeWidth={1.5} />
                <h3>Configurar columnas de evaluación</h3>
                <p>Para puntuar en <strong>{currentApparatusName || 'este aparato'}</strong>, primero configura las columnas de evaluación (D-Score, E-Score, etc.)</p>
                <button className="jse-setup-btn" onClick={() => setShowSetup(true)}>
                  <Settings size={18} /> Configurar ahora
                </button>
              </div>
            ) : (
              <div className="jse-setup-prompt">
                <Settings size={48} strokeWidth={1.5} />
                <h3>Configura tu evaluación</h3>
                <p>Selecciona tu rol de juez, el aparato y la plantilla de puntuación para comenzar a evaluar.</p>
                <button className="jse-setup-btn" onClick={() => setShowSetup(true)}>
                  <Settings size={18} /> Configurar ahora
                </button>
              </div>
            )}

            {selectedApparatus && activeScoreColumns.length > 0 && Object.keys(scores).length > 0 && (
              <div className="save-section">
                <button
                  onClick={handleSaveSectionScores}
                  disabled={savingStatus === 'saving'}
                  className={`save-all-button ${savingStatus}`}
                >
                  {savingStatus === 'saving' && '⏳ Guardando...'}
                  {savingStatus === 'saved' && '✓ Guardado'}
                  {savingStatus === 'error' && '✗ Error'}
                  {!savingStatus && (
                    <>
                      <Save size={18} /> Guardar cambios
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        ) : selectedClub && gymnasts.length === 0 ? (
          <div className="empty-state">
            <Target size={48} />
            <h3>No hay atletas</h3>
            <p>Intenta cambiar los filtros</p>
          </div>
        ) : (
          <div className="empty-state">
            <Target size={48} />
            <h3>Selecciona un club</h3>
            <p>Elige un club para ver sus atletas</p>
          </div>
        )}
      </div>

      {/* Setup Modal */}
      {showSetup && (
        <JudgeEventSetup
          event={event}
          judgeId={judgeInfo?.id}
          onConfigured={handleSetupConfigured}
          onClose={() => setShowSetup(false)}
          isInitialSetup={Object.keys(judgeConfigs).length === 0 && !selectedApparatus}
        />
      )}

      <CategoryFooter />
    </div>
  );
};

export default JudgeScoreEntry;
